import { ReactElement, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Swiper from 'swiper';
import 'swiper/css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import MobileHeroSection from './MobileHeroSection';
import MobilePillowsSection from './MobilePillowsSection';
import MobileBeddingSection from './MobileBeddingSection';
import MobileBathFragranceSection from './MobileBathFragranceSection';
import MobileDiscoveriesSection from './MobileDiscoveriesSection';
import MobileSeoSection from './MobileSeoSection';

type TimelineVarsWithScrollTrigger = gsap.TweenVars & {
  scrollTrigger?: {
    trigger?: Element;
  };
};

const MobileHomePage = (): ReactElement => {
  const { locale } = useRouter();
  const { t } = useTranslation('', { keyPrefix: '' });

  const swiperBeddingRef = useRef<HTMLDivElement>(null);
  const swiperBeddingInstance = useRef<Swiper | null>(null);

  const prodUrl = (value: string): string => t(`$local.$homePageLinks.$product.${value}`);

  const catUrl = (value: string): string => t(`$local.$homePageLinks.$category.${value}`);

  useEffect(() => {
    if (swiperBeddingRef.current && !swiperBeddingInstance.current) {
      swiperBeddingInstance.current = new Swiper(swiperBeddingRef.current, {
        slidesPerView: 1.17,
        spaceBetween: 40,
      });
    }

    return () => {
      if (swiperBeddingInstance.current) {
        swiperBeddingInstance.current.destroy();
        swiperBeddingInstance.current = null;
      }
    };
  }, [locale]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const titlesTl: gsap.core.Timeline[] = [];
    let hpMobile = false;
    let hpTablet = false;

    const initTitleAnimation = () => {
      const titles = document.querySelectorAll('.mobile-bath-fragrance .img-frame h3');
      titles.forEach(title => {
        const titleTl = gsap.timeline({
          scrollTrigger: {
            trigger: title,
            scrub: true,
            end: '+=70%',
          },
        });
        titleTl.fromTo(
          title,
          { top: '74%' },
          {
            top: '15%',
          },
        );
        titlesTl.push(titleTl);
      });
    };

    const checkViewportAnimation = () => {
      const viewportWidth = window.innerWidth;
      if (viewportWidth <= 430) {
        if (!hpMobile) {
          titlesTl.forEach(timeline => {
            timeline.kill();
            const { scrollTrigger } = timeline.vars as TimelineVarsWithScrollTrigger;
            const trigger = scrollTrigger?.trigger as HTMLElement | undefined;
            if (trigger?.style) trigger.style.cssText = '';
          });
          titlesTl.length = 0;
        }
        hpMobile = true;
        hpTablet = false;
      } else {
        if (!hpTablet) {
          initTitleAnimation();
        }
        hpMobile = false;
        hpTablet = true;
      }
    };

    checkViewportAnimation();
    window.addEventListener('resize', checkViewportAnimation);

    setTimeout(() => ScrollTrigger.refresh(), 500);

    return () => {
      window.removeEventListener('resize', checkViewportAnimation);
      titlesTl.forEach(timeline => {
        timeline.kill();
      });
    };
  }, []);

  return (
    <div className="mobile-hp">
      <div role="main" className="mobile-cta">
        <div className="animation-wrapper">
          <MobileHeroSection />
          <MobilePillowsSection prodUrl={prodUrl} />
          <MobileBeddingSection swiperRef={swiperBeddingRef} catUrl={catUrl} />
        </div>
        <MobileBathFragranceSection prodUrl={prodUrl} catUrl={catUrl} />
        <MobileDiscoveriesSection prodUrl={prodUrl} />
        <MobileSeoSection />
      </div>
    </div>
  );
};

export default MobileHomePage;
